import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Award, Loader, AlertCircle, CheckCircle, XCircle, ArrowLeft } from "lucide-react";
import { certificateApi } from "../lib";

interface VerifyResponse {
  isValid: boolean;
  certificateId: string;
  userName: string;
  courseTitle: string;
  issuedAt: string;
  expiresAt?: string;
}

export default function VerifyCertificatePage() {
  const { certificateId } = useParams<{ certificateId: string }>();
  const [result, setResult] = useState<VerifyResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const verify = async () => {
      if (!certificateId) {
        setError("No certificate ID provided.");
        setLoading(false);
        return;
      }

      try {
        const res = await certificateApi.verifyCertificate(certificateId);
        if (res?.success && res.data) {
          setResult(res.data as VerifyResponse);
        } else {
          setError("Invalid certificate.");
        }
      } catch (err) {
        setError("Unable to verify certificate. Please try again later.");
      } finally {
        setLoading(false);
      }
    };

    verify();
  }, [certificateId]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen bg-gray-50">
        <div className="text-center">
          <Loader className="w-8 h-8 mx-auto mb-3 text-orange-500 animate-spin" />
          <p className="text-sm text-gray-500">Verifying certificate...</p>
        </div>
      </div>
    );
  }

  if (error || !result?.isValid) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
        <div className="max-w-md text-center">
          <div className="flex items-center justify-center w-16 h-16 mx-auto mb-4 bg-red-100 rounded-full">
            <XCircle className="w-8 h-8 text-red-500" />
          </div>
          <h2 className="mb-2 text-xl font-semibold text-gray-900">Invalid Certificate</h2>
          <p className="mb-6 text-gray-600">
            {error || "This certificate ID is not recognized or has been revoked."}
          </p>
          <Link
            to="/"
            className="inline-flex items-center gap-2 px-4 py-2 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
          >
            <ArrowLeft className="w-4 h-4" />
            Back to Home
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col items-center justify-center min-h-screen p-4 bg-gray-50">
      <div className="w-full max-w-lg">
        <div className="p-6 bg-white border border-gray-200 rounded-2xl shadow-lg">
          <div className="flex flex-col items-center text-center">
            <div className="flex items-center justify-center w-14 h-14 mb-4 bg-green-100 rounded-full">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="mb-1 text-xl font-bold text-gray-900">Certificate Verified</h2>
            <p className="mb-6 text-sm text-gray-500">
              This is a valid certificate issued by LearnHub.
            </p>

            <div className="w-full p-4 space-y-3 text-left bg-gray-50 rounded-xl">
              <div>
                <p className="text-xs text-gray-500">Recipient</p>
                <p className="text-sm font-medium text-gray-900">{result.userName}</p>
              </div>
              <div>
                <p className="text-xs text-gray-500">Course</p>
                <p className="text-sm font-medium text-gray-900">{result.courseTitle}</p>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <p className="text-xs text-gray-500">Certificate ID</p>
                  <p className="text-xs font-mono text-amber-600 break-all">{result.certificateId}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500">Issued On</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(result.issuedAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              </div>
              {result.expiresAt && (
                <div>
                  <p className="text-xs text-gray-500">Expires On</p>
                  <p className="text-sm font-medium text-gray-900">
                    {new Date(result.expiresAt).toLocaleDateString("en-US", {
                      year: "numeric",
                      month: "long",
                      day: "numeric",
                    })}
                  </p>
                </div>
              )}
            </div>

            <Link
              to="/"
              className="inline-flex items-center gap-2 mt-6 px-4 py-2 text-sm font-medium text-white transition-all rounded-lg bg-gradient-to-r from-orange-600 to-orange-500 hover:shadow-md"
            >
              <ArrowLeft className="w-4 h-4" />
              Back to Home
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
